/**
 * Rendering care timestamps in the VIEWER'S timezone.
 *
 * Observation `date` values are stored as UTC ISO strings ("…T08:00:00Z"), but
 * a carer records at a wall-clock time and a planner reads at one. Every
 * user-facing clock and day boundary must therefore be the local rendering,
 * never the characters inside the ISO string.
 *
 * ☠️ Why this module exists rather than a helper per renderer: the Daily log
 * (charts/monitored-chart) converted properly while the Combined narrative
 * (clients/narrative) sliced the raw string, so the same recording read 09:00
 * in one tab and 08:00 in the other through a British summer. Two renderers of
 * one timestamp drifted apart because each owned its own formatter. One
 * function is the fix; the divergence was the bug.
 */

/** HH:MM in the viewer's timezone. `fallback` is shown for an unparseable date. */
export function localTimeOf(iso: string, fallback = "—"): string {
  const d = new Date(iso);
  return isNaN(d.getTime())
    ? fallback
    : d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
}

/**
 * YYYY-MM-DD for the LOCAL calendar day the instant falls on.
 *
 * ☠️ Not `iso.slice(0, 10)`. Under BST an entry made at 00:30 on the 13th is
 * stored `2026-08-12T23:30:00Z`, so slicing files it under the 12th — the
 * carer's night shift is split across the wrong heading. The shape is kept as
 * YYYY-MM-DD because callers sort on it lexically and re-parse it for headings.
 */
export function localDayKey(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso.slice(0, 10);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
